/**
 * 초대 (지시54).
 *
 * ## 토큰은 **한 번만** 나간다
 *
 * `POST /invites` 응답의 `token` 이 유일한 기회다(통합 브리프 ①). 목록에는 없고 다시
 * 볼 수 없다. 그래서 이 화면은 두 가지를 함께 해야 한다.
 *
 *   ① 그 자리에서 복사시킨다
 *   ② **새로 고치면 사라진다는 것을 말한다** — 말 안 하면 창을 닫고 나서야 안다
 *
 * 닫기 전에 한 번 더 묻지는 않는다. 대신 닫는 단추 문구를 `Done` 으로 두고 경고를
 * 그 옆에 붙였다 — 확인 창을 겹치면 복사한 사람도 한 번 더 눌러야 한다.
 *
 * ## 링크로 준다
 *
 * 토큰만 주면 받는 사람이 어디에 넣을지 모른다. 수락 화면 주소에 붙여 **그대로 보낼 수
 * 있는 것**을 만든다.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '../../../../api/permissions.js';
import { useApiQuery } from '../../lib/useApiQuery.tsx';
import { useApiClient } from '../../lib/useApiClient.tsx';
import { accessKeys } from '../../lib/queryKeys.tsx';
import { describeError } from '../../lib/describeError.tsx';
import { formatTimestamp } from '../../lib/format.tsx';
import { strings } from '../../lib/strings.tsx';
import type { InvitationRow, IssuedInvitationRow, PermissionMapRow } from '../../lib/apiTypes.tsx';
import './access.css';

const t = strings.access;

/** 수락 화면 경로. 한 곳에만 적는다 — 링크와 라우트가 갈라지면 링크가 죽는다. */
export const ACCEPT_PATH = '/accept-invite';

export function inviteLink(token: string, origin: string): string {
  return `${origin}${ACCEPT_PATH}?token=${encodeURIComponent(token)}`;
}

/**
 * 초대가 지금 어떤 상태인가 — **네 갈래**.
 *
 * 순수 함수로 뺀다: 목록에 무엇이 어떤 상태로 나오는지를 값으로 확인하기 위해서다.
 * 폐기와 만료를 한 배지로 말하면 "내가 취소한 것" 과 "시간이 지난 것" 이 같아진다.
 */
export function inviteState(
  invite: InvitationRow,
  now: number,
): 'accepted' | 'revoked' | 'expired' | 'live' {
  if (invite.acceptedAt !== null) return 'accepted';
  if (invite.revokedAt !== null) return 'revoked';
  if (Date.parse(invite.expiresAt) <= now) return 'expired';
  return 'live';
}

function IssuedOnce({ issued, origin, onDone }: { issued: IssuedInvitationRow; origin: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const link = inviteLink(issued.token, origin);

  return (
    <div className="access__token" role="status">
      <strong>{t.inviteTokenTitle}</strong>
      {/* 놓치면 초대를 새로 발급하는 수밖에 없다 — 그 사실을 먼저 말한다. */}
      <p className="access__note">{t.inviteTokenOnce}</p>
      <code>{link}</code>
      <button
        type="button"
        onClick={() => {
          /*
           * 클립보드가 없거나 막힌 환경이 있다(비 HTTPS·권한 거부). 그때도 링크는
           * 위에 글자로 떠 있으므로 **복사 실패가 초대를 잃게 하지는 않는다.**
           * 성공했다고 말하기 전에 확인한다(상시 규칙 ⑥).
           */
          void navigator.clipboard
            ?.writeText(link)
            .then(() => setCopied(true))
            .catch(() => setCopied(false));
        }}
      >
        {copied ? t.inviteCopied : t.inviteCopy}
      </button>
      <button type="button" onClick={onDone}>
        {t.inviteDone}
      </button>
    </div>
  );
}

export function InvitesPanel({ origin = window.location.origin }: { origin?: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [draft, setDraft] = useState<PermissionMapRow>(
    Object.fromEntries(PERMISSIONS.map((p) => [p, p === 'pipeline.read'])),
  );
  /** 발급 응답. **닫기 전까지만** 들고 있다 — 다시 받을 수 없다. */
  const [issued, setIssued] = useState<IssuedInvitationRow | null>(null);

  const invites = useApiQuery({
    queryKey: accessKeys.invites(),
    queryFn: () => api.listInvites(),
    retry: false,
  });

  const create = useMutation({
    mutationFn: () => api.createInvite({ email: email.trim(), permissions: draft }),
    onSuccess: (row) => {
      setIssued(row);
      setEmail('');
      void queryClient.invalidateQueries({ queryKey: accessKeys.invites() });
    },
  });

  const revoke = useMutation({
    mutationFn: (inviteId: string) => api.revokeInvite(inviteId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: accessKeys.invites() }),
  });

  const now = Date.now();

  return (
    <section className="access__panel">
      <h3>{t.invitesTitle}</h3>
      <p className="access__note">{t.invitesHint}</p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim() !== '') create.mutate();
        }}
      >
        <label>
          {t.inviteEmail}{' '}
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        {/* 축은 서버 목록에서 — 화면이 일곱을 외우지 않는다. */}
        {PERMISSIONS.map((p) => (
          <label key={p} className="access__axis" title={t.axisMeaning[p] ?? p}>
            <input
              type="checkbox"
              aria-label={`${t.inviteEmail} — ${p}`}
              checked={draft[p] ?? false}
              onChange={(e) => setDraft({ ...draft, [p]: e.target.checked })}
            />
            {p}
            {/* ⚠ 이름은 남긴다 — 감사 기록·계약이 그 낱말을 쓴다(결정5422). 뜻을 곁들일 뿐이다. */}
            <span className="access__axis-meaning">{t.axisMeaning[p] ?? ''}</span>
          </label>
        ))}
        <button type="submit" disabled={create.isPending || email.trim() === ''}>
          {create.isPending ? t.inviteCreating : t.inviteCreate}
        </button>
      </form>
      {create.isError ? <p role="alert">{describeError(create.error)}</p> : null}

      {issued !== null ? (
        <IssuedOnce issued={issued} origin={origin} onDone={() => setIssued(null)} />
      ) : null}

      {invites.isError ? <p role="alert">{describeError(invites.error)}</p> : null}
      {invites.data !== undefined && invites.data.length === 0 ? <p>{t.invitesEmpty}</p> : null}

      {invites.data !== undefined && invites.data.length > 0 ? (
        <table className="access__table">
          <thead>
            <tr>
              <th scope="col">{t.colEmail}</th>
              <th scope="col">{t.colState}</th>
              <th scope="col">{t.colExpires}</th>
              <th scope="col">{t.colActions}</th>
            </tr>
          </thead>
          <tbody>
            {invites.data.map((inv) => {
              const state = inviteState(inv, now);
              return (
                <tr key={inv.inviteId} data-invite={inv.inviteId} data-state={state}>
                  <th scope="row">{inv.email}</th>
                  <td>{t.inviteStateLabel[state]}</td>
                  {/*
                    ★ **날짜만 잘라 쓰지 않는다** (결정1442).

                    종전에는 `expiresAt.slice(0, 10)` — ISO 앞 열 글자, 즉 **UTC 날짜**다.
                    운영자가 시각 지시에서 짚은 바로 그 함정이다: *"날짜가 넘어가는 것을
                    조심하라."* 만료가 `2026-08-27T21:00Z` 면 화면은 **08-27** 이라
                    적는데 KST 로는 **08-28 06:00** 이다 — 아직 **아홉 시간** 남았는데
                    사람은 어제 끝난 것으로 읽는다.

                    만료는 **시각까지** 보여 준다. 남은 시간이 곧 사람이 할 일을 정한다.
                  */}
                  <td>{formatTimestamp(inv.expiresAt)}</td>
                  <td>
                    {/*
                      살아 있는 것만 폐기할 수 있다. 이미 수락·폐기·만료된 것에 단추를
                      두면 눌러 보고 아무 일도 안 일어난다 — 조용한 실패다.
                    */}
                    {state === 'live' ? (
                      <button
                        type="button"
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate(inv.inviteId)}
                      >
                        {revoke.isPending ? t.inviteRevoking : t.inviteRevoke}
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
      {revoke.isError ? <p role="alert">{describeError(revoke.error)}</p> : null}
    </section>
  );
}
